import React, { useState, useCallback, useRef } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Collapse } from "reactstrap";
import { Button } from "ui-kit-takeat";
import { FaCheck, FaDownload, FaInfoCircle } from "react-icons/fa";
import apiGd from "services/apiGd";

function downloadFile(data, filename) {
  const href = URL.createObjectURL(data);
  const link = document.createElement("a");
  link.href = href;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(href);
}

// Tradução amigável dos motivos de erro retornados pelo backend.
const REASON_LABELS = {
  required_field_missing: "campo obrigatório vazio",
  csosn_must_be_3_digits: "CSOSN precisa ter 3 dígitos",
  cst_must_be_2_digits: "CST precisa ter 2 dígitos",
  invalid_number: "número inválido",
  invalid_integer: "precisa ser um número inteiro",
  invalid_boolean: "preencha com SIM ou NÃO",
};

function reasonToText(reason) {
  if (REASON_LABELS[reason]) return REASON_LABELS[reason];
  const maxLen = /^max_length_(\d+)$/.exec(reason);
  if (maxLen) return `excede o limite de ${maxLen[1]} caracteres`;
  return reason;
}

function formatImportError(err) {
  const data = err?.response?.data || {};
  const baseMsg =
    data.message || "Erro ao importar planilha. Verifique o arquivo.";
  const payload = data.payload || {};
  switch (data.errorType) {
    case "missing_sheet":
      return `${baseMsg} Abas faltando: ${(payload.missing || []).join(", ")}.`;
    case "invalid_header":
      return `${baseMsg} Aba "${payload.sheet}" — colunas faltando: ${(
        payload.missing || []
      ).join(", ")}.`;
    case "regime_column_mismatch":
      return `${baseMsg} Esperado "${payload.expected_column}", veio "${payload.got_column}".`;
    case "duplicate_ids_in_sheet": {
      const p = (payload.duplicate_product_ids || []).join(", ");
      const c = (payload.duplicate_complement_ids || []).join(", ");
      return `${baseMsg}${p ? ` Produtos: ${p}.` : ""}${
        c ? ` Complementos: ${c}.` : ""
      }`;
    }
    case "ids_not_owned": {
      const p = (payload.invalid_product_ids || []).join(", ");
      const c = (payload.invalid_complement_ids || []).join(", ");
      return `${baseMsg}${p ? ` Produtos: ${p}.` : ""}${
        c ? ` Complementos: ${c}.` : ""
      }`;
    }
    case "required_field_missing":
    case "invalid_value": {
      const errors = payload.errors || [];
      const preview = errors
        .slice(0, 6)
        .map((e) => {
          const kindLabel = e.kind === "product" ? "Produto" : "Complemento";
          const rowInfo = e.row ? ` (linha ${e.row})` : "";
          return `${kindLabel} #${e.id}${rowInfo} — "${e.field}": ${reasonToText(
            e.reason
          )}`;
        })
        .join(" | ");
      const more = errors.length > 6 ? ` ... (+${errors.length - 6})` : "";
      return `${baseMsg}. ${preview}${more}`;
    }
    case "invalid_id":
      return `${baseMsg} Linha ${payload.row}: "${payload.value}".`;
    default:
      return baseMsg;
  }
}

// Painel que lista todos os erros de validação da planilha de cardápio,
// agrupados por aba, com a linha e o motivo de cada problema.
function MenuErrorPanel({ errors, onClose }) {
  const groups = errors.reduce((acc, err) => {
    const key = err.sheet || "Planilha";
    if (!acc[key]) acc[key] = [];
    acc[key].push(err);
    return acc;
  }, {});

  const SHEET_ORDER = ["Categorias", "Produtos", "ComplementoCat", "Complementos"];
  const orderedSheets = Object.keys(groups).sort(
    (a, b) =>
      (SHEET_ORDER.indexOf(a) === -1 ? 99 : SHEET_ORDER.indexOf(a)) -
      (SHEET_ORDER.indexOf(b) === -1 ? 99 : SHEET_ORDER.indexOf(b))
  );

  return (
    <div
      style={{
        marginTop: 14,
        border: "1px solid #F5C2C7",
        background: "#FDEDEE",
        borderRadius: 8,
        padding: "12px 14px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <span
          style={{
            fontFamily: "Poppins",
            fontWeight: 600,
            fontSize: 13.5,
            color: "#842029",
          }}
        >
          {errors.length} erro(s) encontrado(s) na planilha — nada foi importado.
        </span>
        <a
          onClick={onClose}
          style={{
            cursor: "pointer",
            color: "#842029",
            fontSize: 12,
            fontWeight: 500,
            whiteSpace: "nowrap",
          }}
        >
          Fechar
        </a>
      </div>

      <div style={{ maxHeight: 260, overflowY: "auto" }}>
        {orderedSheets.map((sheet) => (
          <div key={sheet} style={{ marginBottom: 8 }}>
            <div
              style={{
                fontFamily: "Poppins",
                fontWeight: 600,
                fontSize: 12.5,
                color: "#842029",
                marginBottom: 2,
              }}
            >
              Aba {sheet} ({groups[sheet].length})
            </div>
            <ul
              style={{
                margin: 0,
                paddingLeft: 18,
                fontFamily: "Poppins",
                fontSize: 12.5,
                color: "#5c1c22",
                lineHeight: 1.5,
              }}
            >
              {groups[sheet].map((err, idx) => (
                <li key={idx}>
                  {err.message ||
                    `${err.row ? `Linha ${err.row}: ` : ""}${
                      err.column ? `coluna "${err.column}" — ` : ""
                    }${err.reason || "valor inválido"}`}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function GlossaryItem({ title, children }) {
  return (
    <li style={{ marginBottom: 6 }}>
      <b>{title}</b>
      {children ? <> — {children}</> : null}
    </li>
  );
}

function BrandImportModal({ isOpen, toggle, brand, restaurantId, restaurantName, token, notify }) {
  const [menuFile, setMenuFile] = useState(null);
  const [sheetFile, setSheetFile] = useState(null);
  const [uploadingMenu, setUploadingMenu] = useState(false);
  const [uploadingSheet, setUploadingSheet] = useState(false);
  // Erros detalhados de validação da planilha de cardápio (aba/linha/motivo).
  const [menuErrors, setMenuErrors] = useState([]);

  // Planilha de Produtos e Complementos (export/import por marca)
  const [dataSheetFile, setDataSheetFile] = useState(null);
  const [dataSheetLoading, setDataSheetLoading] = useState(false);
  const [glossaryOpen, setGlossaryOpen] = useState(true);

  const menuInputRef = useRef(null);
  const sheetInputRef = useRef(null);
  const dataSheetInputRef = useRef(null);

  const resetState = useCallback(() => {
    setMenuFile(null);
    setSheetFile(null);
    setDataSheetFile(null);
    setUploadingMenu(false);
    setUploadingSheet(false);
    setDataSheetLoading(false);
    setGlossaryOpen(true);
    setMenuErrors([]);
  }, []);

  const handleToggle = useCallback(() => {
    resetState();
    toggle();
  }, [resetState, toggle]);

  const sendMenuExcel = useCallback(async () => {
    if (!menuFile) {
      notify("tr", "danger", "Selecione um arquivo antes!");
      return;
    }
    setUploadingMenu(true);
    setMenuErrors([]);
    try {
      const formData = new FormData();
      formData.append("file", menuFile, menuFile.name);
      const reqConfig = { headers: { Authorization: `Bearer ${token}` } };

      await apiGd.post(
        `/restaurants/excel-files/${restaurantId}?brand_id=${brand.id}`,
        formData,
        reqConfig
      );

      notify("tr", "success", "Cardápio atualizado com sucesso!");
      setMenuFile(null);
      if (menuInputRef.current) menuInputRef.current.value = "";
    } catch (error) {
      const data = error?.response?.data || {};

      // Validação da planilha: o backend devolve todos os erros de uma vez.
      if (data.errorType === "menu_sheet_validation") {
        const errors = data.payload?.errors || [];
        setMenuErrors(errors);
        notify(
          "tr",
          "danger",
          `A planilha tem ${errors.length} erro(s). Veja os detalhes abaixo do campo de envio.`
        );
      } else {
        const msg =
          data.message ||
          data.error ||
          "Erro ao atualizar cardápio! Confira as informações no excel";
        notify("tr", "danger", msg);
      }
    } finally {
      setUploadingMenu(false);
    }
  }, [menuFile, restaurantId, brand, token, notify]);

  const sendProductInputsExcel = useCallback(async () => {
    if (!sheetFile) {
      notify("tr", "danger", "Selecione um arquivo antes!");
      return;
    }
    setUploadingSheet(true);
    try {
      const formData = new FormData();
      formData.append("file", sheetFile, sheetFile.name);
      const reqConfig = { headers: { Authorization: `Bearer ${token}` } };

      await apiGd.post(
        `/admin/upload/product-inputs-sheet/${restaurantId}?brand_id=${brand.id}`,
        formData,
        reqConfig
      );

      notify("tr", "success", "Ficha técnica atualizada com sucesso!");
      setSheetFile(null);
      if (sheetInputRef.current) sheetInputRef.current.value = "";
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Erro ao atualizar ficha técnica! Confira as informações no excel";
      notify("tr", "danger", msg);
    } finally {
      setUploadingSheet(false);
    }
  }, [sheetFile, restaurantId, brand, token, notify]);

  const downloadProductInputsModel = useCallback(async () => {
    try {
      const reqConfig = {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      };
      const filename = `Ficha_Tecnica_${restaurantName || "modelo"}`;
      const response = await apiGd.get(
        `/admin/download/product-inputs-sheet/${restaurantId}`,
        reqConfig
      );
      downloadFile(response.data, filename);
    } catch (error) {
      notify("tr", "danger", "Erro ao baixar modelo de ficha técnica.");
    }
  }, [restaurantId, restaurantName, token, notify]);

  // ---- Planilha de Produtos e Complementos (por marca) -------------------
  const downloadDataSheet = useCallback(async () => {
    setDataSheetLoading(true);
    try {
      const response = await apiGd.get(
        `/admin/restaurants/${restaurantId}/fiscal-spreadsheet?brand_id=${brand.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );

      const cd = response.headers?.["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match?.[1] || `Cardapio_${brand.fantasy_name || brand.id}.xlsx`;

      downloadFile(response.data, filename);
      notify("tr", "success", "Planilha baixada com sucesso!");
    } catch (err) {
      let message = "Erro ao baixar planilha.";
      try {
        const text = await err?.response?.data?.text?.();
        const parsed = text ? JSON.parse(text) : null;
        if (parsed?.message) message = parsed.message;
      } catch (_) {}
      notify("tr", "danger", message);
    } finally {
      setDataSheetLoading(false);
    }
  }, [restaurantId, brand, token, notify]);

  const sendDataSheet = useCallback(async () => {
    if (!dataSheetFile) {
      notify("tr", "danger", "Selecione um arquivo antes!");
      return;
    }
    if (dataSheetFile.size > 5 * 1024 * 1024) {
      notify("tr", "danger", "Arquivo acima de 5MB.");
      return;
    }
    if (
      !window.confirm(
        `Atenção: qualquer célula deixada VAZIA vai APAGAR (esvaziar) o valor correspondente no banco desta marca (${brand.fantasy_name}). ` +
          "Confirme que só deixou em branco o que realmente deseja limpar. Deseja continuar?"
      )
    ) {
      return;
    }

    setDataSheetLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", dataSheetFile, dataSheetFile.name);

      const response = await apiGd.put(
        `/admin/restaurants/${restaurantId}/fiscal-spreadsheet?brand_id=${brand.id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { updated = {}, skipped = {} } = response.data || {};
      const skippedTotal =
        (skipped.products_no_id || 0) + (skipped.complements_no_id || 0);
      const skippedMsg =
        skippedTotal > 0 ? ` ${skippedTotal} linha(s) sem ID ignorada(s).` : "";

      notify(
        "tr",
        "success",
        `${updated.products || 0} produto(s) e ${
          updated.complements || 0
        } complemento(s) atualizados.${skippedMsg}`
      );
      setDataSheetFile(null);
      if (dataSheetInputRef.current) dataSheetInputRef.current.value = "";
    } catch (err) {
      notify("tr", "danger", formatImportError(err));
    } finally {
      setDataSheetLoading(false);
    }
  }, [dataSheetFile, restaurantId, brand, token, notify]);

  if (!brand) return null;

  const sectionStyle = {
    border: "1px solid #e9ecef",
    borderRadius: 8,
    padding: "20px 20px 16px",
  };
  const sectionTitle = {
    fontFamily: "Poppins",
    fontSize: 15,
    fontWeight: 600,
    color: "#333",
    display: "block",
    marginBottom: 2,
  };
  const sectionSubtitle = {
    fontFamily: "Poppins",
    fontSize: 13,
    color: "#6c6c6c",
    display: "block",
    marginBottom: 16,
  };

  return (
    <Modal isOpen={isOpen} toggle={handleToggle} size="lg">
      <ModalHeader toggle={handleToggle}>
        Importações — {brand.fantasy_name}
      </ModalHeader>
      <ModalBody style={{ padding: "20px 24px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Cardápio */}
          <div style={sectionStyle}>
            <span style={sectionTitle}>Importar Cardápio</span>
            <span style={sectionSubtitle}>
              Importar categorias, produtos e complementos via planilha Excel
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <input
                ref={menuInputRef}
                type="file"
                accept=".xls,.xlsx"
                onChange={(e) => {
                  setMenuFile(e.target.files[0] || null);
                  setMenuErrors([]);
                }}
                style={{ fontFamily: "Poppins", fontSize: 13, flex: 1 }}
              />
              <Button
                title={uploadingMenu ? "Enviando..." : "Enviar"}
                onClick={sendMenuExcel}
                buttonColor="#7a7a7a"
                disabled={uploadingMenu}
                containerStyles={{ height: 36 }}
              />
            </div>

            {menuErrors.length > 0 && (
              <MenuErrorPanel
                errors={menuErrors}
                onClose={() => setMenuErrors([])}
              />
            )}
          </div>

          {/* Ficha Técnica */}
          <div style={sectionStyle}>
            <span style={sectionTitle}>Importar Ficha Técnica</span>
            <span style={sectionSubtitle}>
              Vincular insumos aos produtos e complementos desta marca
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <input
                ref={sheetInputRef}
                type="file"
                accept=".xls,.xlsx"
                onChange={(e) => setSheetFile(e.target.files[0] || null)}
                style={{ fontFamily: "Poppins", fontSize: 13, flex: 1 }}
              />
              <a
                onClick={downloadProductInputsModel}
                style={{
                  cursor: "pointer",
                  fontFamily: "Poppins",
                  fontSize: 12,
                  color: "#007bff",
                  whiteSpace: "nowrap",
                }}
              >
                Baixar modelo
              </a>
              <Button
                title={uploadingSheet ? "Enviando..." : "Enviar"}
                onClick={sendProductInputsExcel}
                buttonColor="#7a7a7a"
                disabled={uploadingSheet}
                containerStyles={{ height: 36 }}
              />
            </div>
          </div>

          {/* Planilha de Produtos e Complementos (dados + fiscal) */}
          <div style={sectionStyle}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <span style={sectionTitle}>Planilha de Produtos e Complementos</span>
              <a
                onClick={() => setGlossaryOpen((v) => !v)}
                style={{
                  cursor: "pointer",
                  color: "#01AFFF",
                  fontSize: 13,
                  fontWeight: 500,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  whiteSpace: "nowrap",
                }}
              >
                <FaInfoCircle /> {glossaryOpen ? "Ocultar glossário" : "Ver glossário e regras"}
              </a>
            </div>
            <span style={sectionSubtitle}>
              Edite em massa preços, disponibilidades, descrições e dados fiscais
              dos produtos e complementos <b>desta marca</b>. Baixe, edite no
              Excel/Sheets e reenvie.
            </span>

            <div
              style={{
                background: "#FFF7E6",
                border: "1px solid #FFE1A6",
                borderRadius: 6,
                padding: "10px 12px",
                fontSize: 12.5,
                color: "#8A6D3B",
                lineHeight: 1.5,
                marginBottom: 14,
              }}
            >
              <b>Atenção:</b> ao reenviar, toda célula deixada <b>em branco</b>{" "}
              <b>apaga (esvazia)</b> o valor correspondente no banco. Só deixe
              vazio o que realmente quer limpar. O envio é <b>atômico</b> (tudo
              ou nada) e só <b>atualiza</b> — nunca cria itens novos.
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <Button
                title="Baixar planilha"
                onClick={downloadDataSheet}
                disabled={dataSheetLoading}
                buttonColor="#01AFFF"
                containerStyles={{ height: 36 }}
              >
                <FaDownload style={{ marginRight: 6 }} /> Baixar planilha
              </Button>
              <input
                ref={dataSheetInputRef}
                type="file"
                accept=".xlsx"
                onChange={(e) => setDataSheetFile(e.target.files[0] || null)}
                style={{ fontFamily: "Poppins", fontSize: 13, flex: 1, minWidth: 180 }}
              />
              <Button
                title={dataSheetLoading ? "Enviando..." : "Enviar"}
                onClick={sendDataSheet}
                buttonColor="#7a7a7a"
                disabled={dataSheetLoading || !dataSheetFile}
                containerStyles={{ height: 36 }}
              >
                <FaCheck />
              </Button>
            </div>

            <Collapse isOpen={glossaryOpen}>
              <div
                style={{
                  marginTop: 16,
                  paddingTop: 14,
                  borderTop: "1px solid #eee",
                  fontFamily: "Poppins",
                  fontSize: 12.5,
                  color: "#444",
                  lineHeight: 1.55,
                }}
              >
                <h6 style={{ fontWeight: 600, marginTop: 0 }}>Como funciona</h6>
                <ol style={{ paddingLeft: 18, marginBottom: 12 }}>
                  <li>
                    Clique em <b>Baixar planilha</b>. Vem um <code>.xlsx</code>{" "}
                    com duas abas: <b>Produtos</b> e <b>Complementos</b>, já com
                    a situação atual <b>desta marca</b>.
                  </li>
                  <li>
                    Edite no Excel/Google Sheets apenas as colunas liberadas.
                  </li>
                  <li>
                    Salve como <code>.xlsx</code>, selecione o arquivo e clique
                    em <b>Enviar</b>.
                  </li>
                </ol>

                <h6 style={{ fontWeight: 600 }}>Regras de importação</h6>
                <ul style={{ paddingLeft: 18, marginBottom: 12 }}>
                  <GlossaryItem title="Vazio = vazio no banco">
                    célula em branco <b>zera/limpa</b> o campo (fica{" "}
                    <code>null</code>). Isso vale para todas as colunas editáveis
                    (preços, textos, SIM/NÃO, fiscais).
                  </GlossaryItem>
                  <GlossaryItem title="Escopo da marca">
                    o envio afeta <b>somente</b> os produtos e complementos da
                    marca selecionada. IDs de outra marca são recusados.
                  </GlossaryItem>
                  <GlossaryItem title="Atômico (tudo ou nada)">
                    se houver <b>qualquer</b> erro, <b>nada</b> é salvo — corrija
                    e reenvie.
                  </GlossaryItem>
                  <GlossaryItem title="Só atualiza, nunca cria">
                    a coluna <b>ID</b> identifica o registro. <b>Linhas sem ID
                    são ignoradas</b> (não criam itens novos).
                  </GlossaryItem>
                  <GlossaryItem title="Não altere ID, Categoria e criação">
                    as colunas de fundo cinza (<b>ID</b> e <b>Categoria/Categorias</b>)
                    são só de referência. Categorias e data de criação{" "}
                    <b>não podem</b> ser alteradas por aqui.
                  </GlossaryItem>
                  <GlossaryItem title="Não renomeie os cabeçalhos">
                    o nome de cada coluna (header) precisa ficar{" "}
                    <b>exatamente igual</b> ao do modelo. A ordem pode variar e
                    colunas extras de anotação são ignoradas.
                  </GlossaryItem>
                  <GlossaryItem title="Campos obrigatórios (com *)">
                    não podem ficar vazios; se ficarem, o envio é bloqueado.
                    São: <b>Nome</b> e <b>Preço de Venda</b> do produto,{" "}
                    <b>Nome</b> do complemento e as disponibilidades marcadas com{" "}
                    <b>*</b>.
                  </GlossaryItem>
                  <GlossaryItem title="SIM / NÃO">
                    colunas de disponibilidade usam <b>SIM</b> ou <b>NÃO</b>{" "}
                    (também aceita 1/0, S/N, X).
                  </GlossaryItem>
                  <GlossaryItem title="Números">
                    aceitam vírgula ou ponto decimal (ex.: <code>17,5</code>).
                    Colunas “(número)” só aceitam inteiros.
                  </GlossaryItem>
                  <GlossaryItem title="Ordem de Exibição começa no 0">
                    <b>
                      a contagem é de zero em diante — o primeiro item é{" "}
                      <code>0</code>, o segundo é <code>1</code>, e assim por
                      diante. Não comece do 1.
                    </b>
                  </GlossaryItem>
                  <GlossaryItem title="NCM / CEST">
                    normalizados: pontos e espaços são removidos (ex.:{" "}
                    <code>2106.90.10</code> → <code>21069010</code>).
                  </GlossaryItem>
                  <GlossaryItem title="CST / CSOSN">
                    depende do regime tributário: Simples Nacional usa{" "}
                    <b>CSOSN</b> (3 dígitos), Regime Normal usa <b>CST</b> (2
                    dígitos). A planilha precisa bater com o regime atual.
                  </GlossaryItem>
                  <GlossaryItem title="Limite do arquivo">
                    até <b>5 MB</b>, formato <code>.xlsx</code>.
                  </GlossaryItem>
                </ul>

                <h6 style={{ fontWeight: 600 }}>O que cada coluna significa</h6>
                <div style={{ fontWeight: 600, margin: "6px 0 2px" }}>Comuns</div>
                <ul style={{ paddingLeft: 18, marginBottom: 8 }}>
                  <GlossaryItem title="Nome">nome exibido no cardápio.</GlossaryItem>
                  <GlossaryItem title="Descrição">texto descritivo do item.</GlossaryItem>
                  <GlossaryItem title="Preço de Venda (R$)">preço padrão (presencial).</GlossaryItem>
                  <GlossaryItem title="Preço Promocional (R$)">preço de promoção (presencial).</GlossaryItem>
                  <GlossaryItem title="Preço no Delivery (R$)">preço usado no delivery.</GlossaryItem>
                  <GlossaryItem title="Preço Promocional no Delivery (R$)">promoção no delivery.</GlossaryItem>
                  <GlossaryItem title="Ativo no Cardápio (SIM/NÃO)">se o item aparece/está ativo no cardápio.</GlossaryItem>
                  <GlossaryItem title="Disponível no Delivery / Autoatendimento / Agendamento">habilita o item em cada canal.</GlossaryItem>
                </ul>
                <div style={{ fontWeight: 600, margin: "6px 0 2px" }}>Só produtos</div>
                <ul style={{ paddingLeft: 18, marginBottom: 8 }}>
                  <GlossaryItem title="Esgotado (SIM/NÃO)">marca o produto como esgotado.</GlossaryItem>
                  <GlossaryItem title="Vendido por Peso (SIM/NÃO)">produto vendido por peso (balança).</GlossaryItem>
                  <GlossaryItem title={'Preço "A partir de" (SIM/NÃO)'}>exibe “a partir de” no preço.</GlossaryItem>
                  <GlossaryItem title="Cobra Taxa de Serviço (SIM/NÃO)">se o item entra na taxa de serviço.</GlossaryItem>
                  <GlossaryItem title="Ordem de Exibição (número)">
                    posição do item na categoria.{" "}
                    <b>Começa no 0 (zero), não no 1.</b>
                  </GlossaryItem>
                  <GlossaryItem title="Código Interno">código interno do item (até 15 caracteres).</GlossaryItem>
                  <GlossaryItem title="Validade (dias)">validade do produto em dias.</GlossaryItem>
                </ul>
                <div style={{ fontWeight: 600, margin: "6px 0 2px" }}>Só complementos</div>
                <ul style={{ paddingLeft: 18, marginBottom: 8 }}>
                  <GlossaryItem title="Limite por Seleção (número)">quantidade máxima permitida.</GlossaryItem>
                  <GlossaryItem title="Aparece em Relatórios (SIM/NÃO)">se o complemento entra nos relatórios.</GlossaryItem>
                </ul>
                <div style={{ fontWeight: 600, margin: "6px 0 2px" }}>Fiscais</div>
                <ul style={{ paddingLeft: 18, marginBottom: 0 }}>
                  <GlossaryItem title="NCM, CEST, CFOP, ICMS, PIS, COFINS, CST/CSOSN, IBS/CBS, CBENEF etc.">
                    parâmetros tributários usados na emissão fiscal. Preencha
                    conforme a orientação contábil do cliente.
                  </GlossaryItem>
                </ul>
              </div>
            </Collapse>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          title="Fechar"
          buttonColor="#6c757d"
          onClick={handleToggle}
          containerStyles={{ height: 36 }}
        />
      </ModalFooter>
    </Modal>
  );
}

export default BrandImportModal;
